import {Decoration, DecorationSource, EditorView, NodeView} from "prosemirror-view";
import {Attrs, DOMSerializer, Fragment, type Node, Slice} from "prosemirror-model";
import {NodeSelection} from "prosemirror-state";

interface SizeOption {
	label: string;
	scale: number;
	// Minimum width of the image
	classes?: string;
}

const SIZE_BUTTONS:SizeOption[] = [
	{ label: "¼", scale: 1/4, classes: "if-big-enough" },
	{ label: "⅓", scale: 1/3, classes: "if-big-enough" },
	{ label: "½", scale: 1/2 },
	{ label: "⅔", scale: 2/3, classes: "if-big-enough" },
	{ label: "¾", scale: 3/4, classes: "if-big-enough" },
	{ label: "1", scale: 1 },
	{ label: "1½", scale: 3/2, classes: "if-big-enough" },
	{ label: "2", scale: 2, classes: "if-big-enough" },
];

export class ImageNodeView implements NodeView{
	dom: HTMLElement;
	private node: Node;
	private view: EditorView;
	private getPos: () => number;

	private image: HTMLImageElement|null;

	private selectionBox: HTMLElement|null;
	private cornerHandle: HTMLElement|null;
	private rightHandle: HTMLElement|null;
	private bottomHandle: HTMLElement|null;
	private sizeInfo: HTMLDivElement|null;
	private sizeWidthInfo: HTMLSpanElement|null;
	private sizeHeightInfo: HTMLSpanElement|null;
	private sizeButtons: HTMLDivElement|null;

	constructor(node:Node, view:EditorView, getPos: ()=>number) {
		this.node = node
		this.view = view
		this.getPos = getPos

		let a = node.attrs;

		let img = document.createElement("img");
		// Propagate attributes
		img.src = a.src;
		img.alt = a.alt || "";
		if (a.title) img.title = a.title;

		let wrapper = document.createElement("span");
		wrapper.contentEditable = "false";
		wrapper.draggable = true
		wrapper.className = "img-wrapper";
		wrapper.appendChild(img);
		wrapper.addEventListener("dragstart", this.onDragStart.bind(this));

		const styles = ImageNodeView.attrsToStyle(a);
		ImageNodeView.applyStyles(wrapper, styles.w);
		ImageNodeView.applyStyles(img, styles.i);

		this.dom = wrapper;
		this.image = img;
	}

	selectNode() {
		this.addResizeUi();
	}

	deselectNode() {
		this.removeResizeUi();
	}

	update(node: Node, decorations: readonly Decoration[], innerDecorations: DecorationSource): boolean {
		if (node.type !== this.node.type) return false;
		this.node = node;
		let a = node.attrs;
		this.image.src = a.src;
		this.image.alt = a.alt || "";
		if (a.title) this.image.title = a.title;
		else this.image.removeAttribute("title");
		const styles = ImageNodeView.attrsToStyle(a);
		ImageNodeView.applyStyles(this.dom, styles.w);
		ImageNodeView.applyStyles(this.image, styles.i);
		console.log("Update applied");
		return true;
	}

	private addResizeUi() {
		const box = document.createElement("div");
		box.className = "img-selection-box";
		this.dom.appendChild(box);
		this.selectionBox = box;

		// Resize info
		this.sizeInfo = document.createElement("div");
		this.sizeInfo.className = "img-size-info";
		this.dom.appendChild(this.sizeInfo);
		this.sizeWidthInfo = document.createElement("span");
		this.sizeWidthInfo.appendChild(document.createTextNode(this.dom.offsetWidth.toString()));
		this.sizeHeightInfo = document.createElement("span");
		this.sizeHeightInfo.appendChild(document.createTextNode(this.dom.offsetHeight.toString()));
		this.sizeInfo.appendChild(this.sizeWidthInfo);
		this.sizeInfo.appendChild(document.createTextNode("×"));
		this.sizeInfo.appendChild(this.sizeHeightInfo);

		// Size buttons
		this.sizeButtons = this.generateSizeButtons();
		this.dom.appendChild(this.sizeButtons);

		let moveListener = null;
		let upListener = null;
		let startScreenWidth = 0;
		let startScreenHeight = 0;
		let startX = 0;
		let startY = 0;

		const right = document.createElement("span");
		right.className = "img-right-handle";
		this.dom.appendChild(right);
		this.rightHandle = right;

		const bottom = document.createElement("span");
		bottom.className = "img-bottom-handle";
		this.dom.appendChild(bottom);
		this.bottomHandle = bottom;

		const corner = document.createElement("span");
		corner.className = "img-corner-handle";
		this.dom.appendChild(corner);
		this.cornerHandle = corner;

		const setImageWidth = (w:number) => {
			this.dom.style.width = w + "px";
			delete this.dom.style.minWidth;
			delete this.dom.style.maxWidth;
			this.image.style.width = w + "px";
			delete this.image.style.minWidth;
			delete this.image.style.maxWidth;
			if (this.sizeWidthInfo) {
				this.sizeWidthInfo.innerText = Math.round(w).toString();
			}
		}

		const setImageHeight = (h:number) => {
			this.dom.style.height = h + "px";
			delete this.dom.style.minHeight;
			delete this.dom.style.maxHeight;
			this.image.style.height = h + "px";
			delete this.image.style.minHeight;
			delete this.image.style.maxHeight;
			if (this.sizeHeightInfo) {
				this.sizeHeightInfo.innerText = Math.round(h).toString();
			}
		}

		const setImageSize = (w:number, h:number, preserveAspectRatio:boolean, preserveHeight = false, propagate = false) => {
			window.requestAnimationFrame(() => {
				if (preserveAspectRatio && this.image.naturalWidth > 0 && this.image.naturalHeight > 0) {
					const ratio = this.image.naturalWidth / this.image.naturalHeight;
					if (preserveHeight) {
						// Keep height and calculate width
						w = h * ratio;
					} else {
						// Keep width and calculate height
						h = w / ratio;
					}
				}
				if (w < 10) w = 10;
				if (h < 10) h = 10;
				if (propagate) {
					w = Math.round(w);
					h = Math.round(h);
				}
				setImageWidth(w);
				setImageHeight(h);
				if (propagate) {
					// Update document
					const tr = this.view.state.tr.setNodeMarkup(this.getPos(), undefined, {
						...this.node.attrs,
						width: w+"px",
						height: h+"px",
						minWidth: null,
						minHeight: null,
						maxWidth: null,
						maxHeight: null
					});
					this.view.dispatch(tr);
					this.view.dispatch(this.view.state.tr.setSelection(NodeSelection.create(this.view.state.doc, this.getPos())));
				}
			});
		};

		const makeHandleListener = (handle:HTMLElement, horizontal:boolean, vertical:boolean) => {
			handle.addEventListener("pointerdown", (e:PointerEvent) => {
				e.preventDefault();
				e.stopPropagation();
				handle.setPointerCapture(e.pointerId);
				startScreenWidth = this.image.offsetWidth;
				startScreenHeight = this.image.offsetHeight;
				startX = e.clientX;
				startY = e.clientY;
				let preserveAspectRatio = horizontal && vertical && !e.shiftKey;
				let preserveHeight = false;

				//console.log("Pointer down: size = "+this.image.offsetWidth+" x "+this.image.offsetHeight+" image = "+this.image.naturalWidth+" x "+this.image.naturalHeight);
				moveListener = (e:PointerEvent) => {
					let diffX = horizontal ? e.clientX - startX : 0;
					let diffY = vertical ? e.clientY - startY : 0;
					setImageSize(startScreenWidth + diffX, startScreenHeight + diffY, preserveAspectRatio, preserveHeight);
				};
				handle.addEventListener("pointermove", moveListener);
				upListener = (e:PointerEvent) => {
					let diffX = horizontal ? e.clientX - startX : 0;
					let diffY = vertical ? e.clientY - startY : 0;
					setImageSize(startScreenWidth + diffX, startScreenHeight + diffY, preserveAspectRatio, preserveHeight, true);
					handle.removeEventListener("pointermove", moveListener);
					handle.removeEventListener("pointerup", upListener);
				};
				handle.addEventListener("pointerup", upListener);
			});
		};

		makeHandleListener(this.cornerHandle, true, true);
		makeHandleListener(this.rightHandle, true, false);
		makeHandleListener(this.bottomHandle, false, true);
	}

	private scaleImage(scale:number) {
		let w = Math.round(this.image.naturalWidth * scale);
		let h = Math.round(this.image.naturalHeight * scale);
		const tr = this.view.state.tr.setNodeMarkup(this.getPos(), undefined, {
			...this.node.attrs,
			width: w+"px",
			height: h+"px",
			minWidth: null,
			minHeight: null,
			maxWidth: null,
			maxHeight: null
		});
		this.view.dispatch(tr);
		this.view.dispatch(this.view.state.tr.setSelection(NodeSelection.create(this.view.state.doc, this.getPos())));
	}

	private generateSizeButtons():HTMLDivElement {
		const buttons = document.createElement("div");
		buttons.className = "img-scale-buttons";
		for (let b of SIZE_BUTTONS) {
			const btn = document.createElement("span");
			btn.className = "img-scale-button"+(b.classes ? " "+b.classes : "");
			btn.innerText = b.label;
			buttons.appendChild(btn);
			btn.addEventListener("click", (e:MouseEvent) => {
				e.preventDefault();
				e.stopPropagation();
				this.scaleImage(b.scale);
			});
		}
		return buttons;
	}

	private removeResizeUi() {
		if (this.sizeInfo) {
			this.dom.removeChild(this.sizeInfo);
			this.sizeInfo = null;
		}
		if (this.sizeButtons) {
			this.dom.removeChild(this.sizeButtons);
			this.sizeButtons = null;
		}
		if (this.selectionBox) {
			this.dom.removeChild(this.selectionBox);
			this.selectionBox = null;
		}
		if (this.cornerHandle) {
			this.dom.removeChild(this.cornerHandle);
			this.cornerHandle = null;
		}
		if (this.rightHandle) {
			this.dom.removeChild(this.rightHandle);
			this.rightHandle = null;
		}
		if (this.bottomHandle) {
			this.dom.removeChild(this.bottomHandle);
			this.bottomHandle = null;
		}
	}

	static applyStyles(el: HTMLElement, styles: Partial<CSSStyleDeclaration>):void {
		for (const [key, value] of Object.entries(styles)) {
			if (value != null) {
				(el.style as any)[key] = value
			}
		}
	}

	static attrsToStyle(attrs: Attrs):{ w:Partial<CSSStyleDeclaration>, i:Partial<CSSStyleDeclaration>} {
		const w: Partial<CSSStyleDeclaration> = {};
		const i: Partial<CSSStyleDeclaration> = {};

		const widthPercent = ImageNodeView.percentAttr(attrs.width);
		if (widthPercent > 0) {
			w.width = attrs.width;
			i.width = "100%";	// 100% of the wrapper
		} else if (widthPercent == 0) {
			w.width = attrs.width;
			i.width = attrs.width;
		} else {
			w.width = "";
			i.width = "";
		}

		const heightPercent = ImageNodeView.percentAttr(attrs.height);
		if (heightPercent > 0) {
			w.height = attrs.height;
			i.height = "100%";	// 100% of the wrapper
		} else if (heightPercent == 0) {
			w.height = attrs.height;
			i.height = attrs.height;
		} else {
			w.height = "";
			i.height = "";
		}

		const maxWidthPercent = ImageNodeView.percentAttr(attrs.maxWidth);
		if (maxWidthPercent > 0) {
			w.maxWidth = attrs.maxWidth;
			i.maxWidth = "100%";
		} else if (heightPercent == 0) {
			i.maxWidth = w.maxWidth = attrs.maxWidth;
		} else {
			i.maxWidth = w.maxWidth = "";
		}

		const maxHeightPercent = ImageNodeView.percentAttr(attrs.maxHeight);
		if (maxHeightPercent > 0) {
			w.maxHeight = attrs.maxHeight;
			i.maxHeight = "100%";
		} else if (heightPercent == 0) {
			i.maxHeight = w.maxHeight = attrs.maxHeight;
		} else {
			i.maxHeight = w.maxHeight = "";
		}

		return { w, i };
	}

	static percentAttr(css:string|null):number {
		if (css == null) return -1;
		//console.dir(css);
		let pidx = css.indexOf("%");
		if (pidx > 0) return parseFloat(css.substring(0, pidx));
		return 0;
	}

	onDragStart(e: DragEvent) {
		if (!e.dataTransfer) return;
		const { view, node } = this;
		const slice = new Slice(Fragment.from(node), 0, 0);
		e.dataTransfer.setData("application/x-prosemirror-slice", JSON.stringify(slice.toJSON()));

		const serializer = DOMSerializer.fromSchema(view.state.schema);
		const domFragment = serializer.serializeNode(node);
		const tempSpan = document.createElement("span");
		tempSpan.appendChild(domFragment);
		e.dataTransfer.setData("text/html", tempSpan.innerHTML);
	}
}